/**
 * Admin Translations editor — list, search, edit, retranslate.
 *
 * Pages 50 rows at a time. Filters by language (5 supported locales).
 * Free-text search runs server-side against the English source.
 * Saving sets `manual=true` so the override sticks across re-translations.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Edit3, Trash2, Save, X, RefreshCw } from "lucide-react";

const LANG_OPTIONS = [
  { value: "", label: "All Languages" },
  { value: "id", label: "Bahasa Indonesia (ID)" },
  { value: "zh-cn", label: "中文 (CN)" },
  { value: "th", label: "ไทย (TH)" },
  { value: "ar", label: "العربية (AR)" },
];

const PAGE_SIZE = 50;

const TranslationsEditor = () => {
  const [lang, setLang] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [editing, setEditing] = useState(null); // {id, source, lang, value, manual}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (lang) params.set("lang", lang);
      if (q) params.set("q", q);
      const { data: res } = await api.get(`/admin/translations?${params.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load translations");
    } finally {
      setBusy(false);
    }
  }, [lang, q, offset]);

  useEffect(() => { load(); }, [load]);

  // Debounced search → reset offset whenever the user types.
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setQ(qInput); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const save = async () => {
    if (!editing || !editing.value.trim()) return;
    try {
      await api.put(`/admin/translations/${encodeURIComponent(editing.id)}`, { value: editing.value });
      toast.success("Override saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const purge = async (row) => {
    if (!window.confirm(`Delete cached translation for "${row.source.slice(0, 60)}..."?`)) return;
    try {
      await api.delete(`/admin/translations/${encodeURIComponent(row.id)}`);
      toast.success("Deleted — will regenerate on next read");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div data-testid="admin-translations" className="space-y-6 pb-32">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/50 block mb-2">Language</label>
          <select
            data-testid="tx-lang-filter"
            value={lang}
            onChange={(e) => { setOffset(0); setLang(e.target.value); }}
            className="bg-transparent border border-white/20 px-4 py-2.5 text-white focus:outline-none focus:border-gold min-w-[240px]"
          >
            {LANG_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-noir">{o.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/50 block mb-2">Search English Source</label>
          <input
            data-testid="tx-search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="e.g. cookie, checkout, ruby…"
            className="w-full bg-transparent border border-white/20 px-4 py-2.5 text-white focus:outline-none focus:border-gold"
          />
        </div>
        <button onClick={load} className="border border-white/30 hover:border-gold text-white hover:text-gold px-5 py-2.5 font-accent text-[10px] tracking-[0.22em] uppercase">
          <RefreshCw size={12} className="inline mr-2" /> Reload
        </button>
      </div>

      <div className="text-white/50 text-sm font-accent tracking-[0.18em] uppercase">
        {data.total} {data.total === 1 ? "entry" : "entries"} · Page {page} / {totalPages} {busy && "· Loading…"}
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {data.rows.map((row) => (
          <div key={row.id} data-testid={`tx-row-${row.id}`} className="px-4 py-3 flex flex-col md:flex-row md:items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-gold">{row.lang}</span>
                {row.manual && <span className="font-accent text-[9px] tracking-[0.22em] uppercase bg-gold/20 text-gold px-2 py-0.5">Manual</span>}
              </div>
              <div className="text-white/45 text-xs mb-1 leading-relaxed line-clamp-2">{row.source}</div>
              <div className="text-ivory text-sm leading-relaxed">{row.value}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setEditing({ ...row })}
                data-testid={`tx-edit-${row.id}`}
                className="border border-white/15 hover:border-gold text-white/70 hover:text-gold px-3 py-2 transition-colors"
                title="Edit override"
              ><Edit3 size={14} /></button>
              <button
                onClick={() => purge(row)}
                data-testid={`tx-delete-${row.id}`}
                className="border border-white/15 hover:border-red-500 text-white/70 hover:text-red-500 px-3 py-2 transition-colors"
                title="Delete cached translation (regenerates next read)"
              ><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {data.rows.length === 0 && !busy && (
          <div className="p-12 text-center text-white/40 font-light">No cached translations match the current filter.</div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2"
        >← Previous</button>
        <button
          disabled={offset + PAGE_SIZE >= data.total}
          onClick={() => setOffset(offset + PAGE_SIZE)}
          className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2"
        >Next →</button>
      </div>

      {/* Edit modal */}
      {editing && (
        <div data-testid="tx-edit-modal" className="fixed inset-0 z-[110] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-xl bg-noir border border-gold/30 shadow-2xl">
            <div className="px-7 pt-7 pb-4 border-b border-white/10 flex items-start justify-between gap-4">
              <div>
                <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-1">Edit Translation · {editing.lang.toUpperCase()}</div>
                <div className="text-white/50 text-sm leading-relaxed">{editing.source}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="px-7 py-6">
              <label className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/50 block mb-2">Translation</label>
              <textarea
                data-testid="tx-edit-textarea"
                rows={6}
                value={editing.value}
                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold leading-relaxed"
              />
              <p className="text-white/40 text-xs font-light mt-2">Saving marks this entry as a manual override — it will never be auto-regenerated.</p>
            </div>
            <div className="px-7 pb-7 flex items-center justify-end gap-3">
              <button onClick={() => setEditing(null)} className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-white px-4 py-3">Cancel</button>
              <button
                onClick={save}
                data-testid="tx-edit-save"
                className="bg-gold text-noir font-accent text-[10px] tracking-[0.28em] uppercase px-7 py-3 hover:bg-gold/90"
              ><Save size={12} className="inline mr-2" /> Save Override</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationsEditor;
